import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import { Modal } from '../../components';
import PriceDisplay from '../PriceDisplay/PriceDisplay';
import css from './AuctionBuyBox.module.css';
import Icons from '../Icons/Icons';
import { normalizeMoney } from '../../util/genericHelpers';
import { getTimedSaleEndUnix } from '../../util/timedSales';
import ContactChevalierForm from '../ContactChevalierForm/ContactChevalierForm';
import { useContactChevalierRequest } from '../../hooks/useContactChevalierRequest';
import { getBidUnitLabels } from '../../util/bidPricing';
import AuctionPricingSummary from '../AuctionPricingSummary/AuctionPricingSummary';

const getCountdownParts = targetMs => {
  const diff = Math.max(0, targetMs - Date.now());
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  const hrs = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  return { days, hrs, mins, secs, isEnded: diff === 0 };
};

const formatMoney = (money, intl) => {
  if (!money?.currency || typeof money?.amount !== 'number') return '--';
  return intl.formatNumber(money.amount / 100, {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: 0,
  });
};

const AuctionBuyBox = props => {
  const {
    listing,
    userBids,
    publicData,
    marketplaceCurrency,
    currentUser,
    intl,
    onPlaceBid,
    isBidDisabled,
    showSellerCommission = false,
    onManageDisableScrolling,
  } = props;

  const auctionEndUnix = getTimedSaleEndUnix(publicData);
  const auctionEndMs = auctionEndUnix ? auctionEndUnix * 1000 : 0;
  const auctionActive = auctionEndMs > Date.now();
  const [nowTick, setNowTick] = useState(0);
  const {
    closeModal: closeContactModal,
    error: contactError,
    initialValues: contactInitialValues,
    inProgress: contactInProgress,
    isModalOpen: isContactModalOpen,
    openModal: openContactModal,
    submitContactRequest,
    success: contactSuccess,
  } = useContactChevalierRequest({ listing, currentUser });

  useEffect(() => {
    if (!auctionActive) return;
    const id = setInterval(() => setNowTick(tick => tick + 1), 1000);
    return () => clearInterval(id);
  }, [auctionActive]);

  const countdown = useMemo(() => getCountdownParts(auctionEndMs), [auctionEndMs, nowTick]);
  const unitLabels = getBidUnitLabels(listing);

  const bids = Array.isArray(userBids) ? userBids : [];
  const highestBid = bids.reduce((highest, bid) => {
    const amount = Number(bid?.biddingAmount);
    if (!Number.isFinite(amount)) return highest;
    if (!highest) return bid;
    return amount > Number(highest?.biddingAmount || 0) ? bid : highest;
  }, null);

  const hasBids = !!highestBid;
  const highestBidMoney = normalizeMoney(
    highestBid
      ? { amount: Number(highestBid?.biddingAmount), currency: highestBid?.biddingCurrency }
      : null,
    marketplaceCurrency
  );

  const reserveMoney = normalizeMoney(publicData?.reservePrice, marketplaceCurrency);

  const minBidIncrement = Number(publicData?.minBidIncrement);
  const minBidIncrementValue = Number.isFinite(minBidIncrement) ? minBidIncrement : 1;
  const minimumBidMoney = hasBids
    ? normalizeMoney(
        {
          amount: Number(highestBidMoney?.amount || 0) + minBidIncrementValue,
          currency: highestBidMoney?.currency || marketplaceCurrency,
        },
        marketplaceCurrency
      )
    : normalizeMoney(publicData?.minimumBid || publicData?.startingBid, marketplaceCurrency) ||
      reserveMoney;

  const currentUserBid = bids
    .filter(bid => bid?.bidderId && bid.bidderId === currentUser?.id?.uuid)
    .reduce((highest, bid) => {
      const amount = Number(bid?.biddingAmount);
      if (!Number.isFinite(amount)) return highest;
      if (!highest) return bid;
      return amount > Number(highest?.biddingAmount || 0) ? bid : highest;
    }, null);

  const currentUserBidMoney = normalizeMoney(
    currentUserBid
      ? { amount: Number(currentUserBid?.biddingAmount), currency: currentUserBid?.biddingCurrency }
      : null,
    marketplaceCurrency
  );

  const reserveMet =
    !!highestBidMoney &&
    !!reserveMoney &&
    highestBidMoney.currency === reserveMoney.currency &&
    highestBidMoney.amount >= reserveMoney.amount;

  const endsLabel = auctionEndMs
    ? intl.formatDate(auctionEndMs, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
      })
    : '-';

  const handlePlaceBid = () => {
    if (onPlaceBid) {
      onPlaceBid({
        listingId: listing?.id,
        minimumBidMoney,
        isEnded: countdown.isEnded,
      });
    }
  };

  const isSellerView =
    showSellerCommission ||
    (currentUser?.id?.uuid && listing?.author?.id?.uuid === currentUser.id.uuid);
  const bidInfoPriceNode = value => <PriceDisplay className={css.bidInfoValue} value={value} />;
  const headingPriceNode = value => <PriceDisplay value={value} />;

  return (
    <section className={css.root}>
      <div className={css.orderDetailsSec}>
        {auctionEndUnix ? (
          <div className={css.timeRemainingSec}>
            <div className={css.time}>
              <span>
                <FormattedMessage id="OrderPanel.timeRemaining" defaultMessage="Time Remaining" />
              </span>

              <span className={css.endsLabel}>
                {countdown.isEnded ? (
                  <FormattedMessage id="OrderPanel.ended" defaultMessage="Ended" />
                ) : (
                  <FormattedMessage
                    id="OrderPanel.endsOn"
                    defaultMessage="Ends {dateLabel}"
                    values={{ dateLabel: endsLabel }}
                  />
                )}
              </span>
            </div>
            {!countdown.isEnded ? (
              <div className={css.timerSec}>
                <div className={css.timeCount}>
                  <span className={css.count}>{String(countdown.days).padStart(2, '0')}</span>
                  <span className={css.timerLabel}>
                    <FormattedMessage id="OrderPanel.days" defaultMessage="Days" />
                  </span>
                </div>

                <div className={css.timeCount}>
                  <span className={css.count}>{String(countdown.hrs).padStart(2, '0')}</span>
                  <span className={css.timerLabel}>
                    <FormattedMessage id="OrderPanel.hrs" defaultMessage="HRS" />
                  </span>
                </div>

                <div className={css.timeCount}>
                  <span className={css.count}>{String(countdown.mins).padStart(2, '0')}</span>
                  <span className={css.timerLabel}>
                    <FormattedMessage id="OrderPanel.mins" defaultMessage="Mins" />
                  </span>
                </div>

                <div className={css.timeCount}>
                  <span className={css.count}>{String(countdown.secs).padStart(2, '0')}</span>
                  <span className={css.timerLabel}>
                    <FormattedMessage id="OrderPanel.secs" defaultMessage="Secs" />
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {hasBids ? (
          <div className={css.bidInfoRow}>
            <div className={css.bidInfoText}>
              <span className={css.bidInfoLabel}>
                <FormattedMessage
                  id={
                    currentUserBidMoney
                      ? 'OrderPanel.yourBidPerUnitLabel'
                      : 'OrderPanel.nextBidPerUnitLabel'
                  }
                  defaultMessage={
                    currentUserBidMoney ? 'Your bid per {unitLabel}' : 'Next bid per {unitLabel}'
                  }
                  values={{ unitLabel: unitLabels.singular }}
                />
              </span>
              {bidInfoPriceNode(formatMoney(currentUserBidMoney || minimumBidMoney, intl))}
            </div>
            <div className={css.bidInfoText}>
              <span className={css.bidInfoLabel}>
                <FormattedMessage
                  id="OrderPanel.minimumBidPerUnitLabel"
                  defaultMessage="Minimum bid per {unitLabel}"
                  values={{ unitLabel: unitLabels.singular }}
                />
              </span>
              {bidInfoPriceNode(formatMoney(minimumBidMoney, intl))}
            </div>
          </div>
        ) : null}
        <div className={css.currentHighestBidSec}>
          <span>
            <FormattedMessage
              id="OrderPanel.currentHighestBidPerUnit"
              defaultMessage="Current Highest Bid per {unitLabel}"
              values={{ unitLabel: unitLabels.singular }}
            />
          </span>
          <h2>
            {hasBids ? headingPriceNode(formatMoney(highestBidMoney, intl)) : null}
            {hasBids && highestBidMoney?.currency ? (
              <sub className={css.currency}>{highestBidMoney.currency}</sub>
            ) : null}
            {!hasBids ? (
              <span className={css.noBids}>
                <FormattedMessage id="OrderPanel.noBids" defaultMessage="No bids yet" />
              </span>
            ) : null}
            {!hasBids && (highestBidMoney?.currency || marketplaceCurrency) ? (
              <span className={css.noBidsCurrency}>
                {highestBidMoney?.currency || marketplaceCurrency}
              </span>
            ) : null}
          </h2>
          <p>
            <Icons name="checkListIcon" />
            {reserveMoney ? (
              reserveMet ? (
                <FormattedMessage id="OrderPanel.reserveMet" defaultMessage="Reserve price met" />
              ) : (
                <FormattedMessage id="OrderPanel.reserveNotMet" defaultMessage="Reserve not met" />
              )
            ) : (
              <FormattedMessage id="OrderPanel.reserveNotSet" defaultMessage="Reserve not set" />
            )}
          </p>
        </div>
        <div className={css.placeBidSec}>
          <div className={css.minBidSec}>
            <FormattedMessage
              id="OrderPanel.minimumBidPerUnit"
              defaultMessage="Minimum bid per {unitLabel}: {minBid}"
              values={{
                unitLabel: unitLabels.singular,
                minBid: bidInfoPriceNode(formatMoney(minimumBidMoney, intl)),
              }}
            />
            {/* <InlineTextButton className={css.autoBidLink} onClick={() => {}}>
              <FormattedMessage id="OrderPanel.setAutoBid" defaultMessage="Set Auto-Bid" />
            </InlineTextButton> */}
          </div>
          <AuctionPricingSummary
            listing={listing}
            userBids={bids}
            publicData={publicData}
            marketplaceCurrency={marketplaceCurrency}
            intl={intl}
            showSellerCommission={isSellerView}
          />
          <div className={css.placeBidBtn}>
            <PrimaryButton
              className={css.placeBidButton}
              disabled={isBidDisabled}
              onClick={handlePlaceBid}
            >
              <FormattedMessage id="OrderPanel.placeBid" defaultMessage="Place Bid" />
            </PrimaryButton>
          </div>
        </div>
      </div>
      <div className={css.supportWrapper}>
        <h2 className={css.supportTitle}>
          <FormattedMessage id="ContactChevalierForm.supportHeading" />
        </h2>
        <p className={css.supportText}>
          <FormattedMessage id="ContactChevalierForm.supportBody" />
        </p>
        <SecondaryButton className={css.supportLink} onClick={openContactModal}>
          <FormattedMessage id="ContactChevalierForm.supportCta" />
        </SecondaryButton>
        {contactSuccess ? (
          <p className={css.supportSuccess}>
            <FormattedMessage id="ContactChevalierForm.submitSuccess" />
          </p>
        ) : null}
      </div>
      <div className={css.protectionSec}>
        <div className={css.protectionType}>
          <h2>
            <FormattedMessage
              id="OrderPanel.buyerProtection.title"
              defaultMessage="Buyer Protection"
            />
          </h2>
          <p>
            <FormattedMessage
              id="OrderPanel.buyerProtection.desc"
              defaultMessage="Funds held in escrow until transfer."
            />
          </p>
        </div>
        <div className={css.protectionType}>
          <h2>
            <FormattedMessage
              id="OrderPanel.insuredShipping.title"
              defaultMessage="Insured Shipping"
            />
          </h2>
          <p>
            <FormattedMessage
              id="OrderPanel.insuredShipping.desc"
              defaultMessage="Global logistics partners available."
            />
          </p>
        </div>
      </div>
      <Modal
        id="AuctionBuyBoxContactChevalierModal"
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ContactChevalierForm
          initialValues={contactInitialValues}
          onSubmit={submitContactRequest}
          inProgress={contactInProgress}
          error={contactError}
        />
      </Modal>
    </section>
  );
};

export default AuctionBuyBox;
